import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { SERVICES } from "@/data/content";

export default function ServicesPreview() {
  const featured = SERVICES.find((s) => s.slug === "debt-advisory");
  const rest = SERVICES.filter((s) => s.slug !== "debt-advisory");

  return (
    <section data-testid="services-preview" className="py-20 sm:py-28 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow>What we arrange</Eyebrow>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-ink max-w-xl leading-[1.1]">
              Nine disciplines. One advisory relationship.
            </h2>
            <Link
              to="/services"
              data-testid="services-view-all"
              className="group inline-flex items-center gap-2 text-sm font-semibold text-gold-700 hover:text-gold-600 transition-colors shrink-0"
            >
              View all services
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <Reveal className="sm:col-span-2">
            <Link
              to={`/book?service=${featured.slug}`}
              data-testid={`service-card-${featured.slug}`}
              className="group relative block h-full rounded-3xl bg-obsidian text-white p-8 sm:p-10 overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:shadow-gold-glow grain"
            >
              <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-gold/15 blur-3xl pointer-events-none" />
              <featured.icon size={30} className="text-gold mb-8" strokeWidth={1.6} />
              <h3 className="font-display text-2xl sm:text-3xl font-bold mb-3">{featured.title}</h3>
              <p className="text-white/60 max-w-md leading-relaxed">{featured.short}</p>
              <span className="inline-flex items-center gap-2 mt-8 text-sm font-semibold text-gold">
                Discuss with Anu
                <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </Link>
          </Reveal>

          {rest.map((s, i) => (
            <Reveal key={s.slug} delay={0.06 * (i % 3)}>
              <Link
                to={`/book?service=${s.slug}`}
                data-testid={`service-card-${s.slug}`}
                className="group block h-full rounded-3xl bg-white border border-ink/8 p-8 transition-all duration-500 hover:-translate-y-2 hover:border-gold/60 hover:shadow-lift"
              >
                <s.icon size={26} className="text-brand mb-6 transition-colors duration-300 group-hover:text-gold-600" strokeWidth={1.6} />
                <h3 className="font-display text-xl font-semibold text-ink mb-2.5">{s.title}</h3>
                <p className="text-sm text-ink/55 leading-relaxed">{s.short}</p>
                <span className="inline-flex items-center gap-1.5 mt-6 text-xs font-semibold uppercase tracking-widest text-ink/35 group-hover:text-gold-600 transition-colors">
                  Book about this <ArrowUpRight size={13} />
                </span>
              </Link>
            </Reveal>
          ))}

          <Reveal delay={0.12} className="sm:col-span-2">
            <Link
              to="/book"
              data-testid="services-preview-cta-tile"
              className="group flex items-center justify-between h-full rounded-3xl bg-gold px-8 sm:px-10 py-8 transition-all duration-500 hover:-translate-y-2 hover:shadow-gold-glow"
            >
              <div>
                <div className="font-display text-2xl font-bold text-obsidian">Not sure where you fit?</div>
                <div className="text-sm text-obsidian/70 mt-1">Book a free 60-minute conversation and we'll map it together.</div>
              </div>
              <ArrowUpRight size={28} className="text-obsidian shrink-0 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
